import { FieldError } from 'react-hook-form';
import React, { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';

import { mergeRefs } from 'shared/react-utils';
import NvPopover from 'shared/components/nv-popover';
import ValidationErrorMessage from 'shared/components/inputs/validation-error-message';

const windowVariable = window as any;

/**
 * Loads the Google Maps JavaScript API if not already loaded.
 * Uses the async loading pattern required by PlaceAutocompleteElement.
 */
const loadGoogleMapsScript = (key: string): Promise<void> => {
  if (windowVariable.google?.maps) return Promise.resolve();

  if (document.getElementById('google_script')) {
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (windowVariable.google?.maps) {
          clearInterval(check);
          resolve();
        }
      }, 100);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'google_script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });
};

type Props = {
  value?: string,
  required?: boolean,
  className?: string,
  error?: FieldError,
  readOnly?: boolean,
  placeholder?: string,
  types?: string[],
  allowManualEntry?: boolean,
  onChange?: (location: string) => void,
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void,
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void,
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void,
};

const NvGooglePlacesAutocomplete = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    readOnly,
    className,
    placeholder,
    types,
    allowManualEntry = false,
    required = false,
    error: propsError,
    value: propsValue,
    onBlur = () => {},
    onFocus = () => {},
    onChange = () => {},
    onKeyDown = () => {},
  } = props;

  const [shadowValue, setShadowValue] = React.useState(propsValue || '');
  const googleKey = useSelector((state) => state.app.configKey.google);

  const hasInvalidValueRef = React.useRef(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const autocompleteElementRef = React.useRef<any>(null);
  const fallbackInputRef = React.useRef<HTMLInputElement>(null);
  const [touched, setTouched] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [googleLoadFailed, setGoogleLoadFailed] = React.useState(false);

  const onChangeRef = React.useRef(onChange);
  const onBlurRef = React.useRef(onBlur);
  const onFocusRef = React.useRef(onFocus);
  const onKeyDownRef = React.useRef(onKeyDown);
  React.useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  React.useEffect(() => { onBlurRef.current = onBlur; }, [onBlur]);
  React.useEffect(() => { onFocusRef.current = onFocus; }, [onFocus]);
  React.useEffect(() => { onKeyDownRef.current = onKeyDown; }, [onKeyDown]);

  const getInternalInput = React.useCallback((): HTMLInputElement | null => {
    const element = autocompleteElementRef.current;
    if (!element) return null;
    return element.shadowRoot?.querySelector('input') ?? element.querySelector?.('input') ?? null;
  }, []);

  React.useImperativeHandle(ref, () => {
    if (readOnly || googleLoadFailed) {
      return fallbackInputRef.current as HTMLInputElement;
    }
    const input = getInternalInput();
    if (input) return input;
    return { focus: () => autocompleteElementRef.current?.focus() } as unknown as HTMLInputElement;
  });

  React.useEffect(() => {
    if (readOnly) return undefined;

    let mounted = true;

    async function initAutocomplete() {
      try {
        await loadGoogleMapsScript(googleKey);
        if (!mounted || !containerRef.current) return;

        const { PlaceAutocompleteElement } = await windowVariable.google.maps.importLibrary('places');
        if (!mounted || !containerRef.current) return;

        const elementOptions: any = {};
        if (types?.length) {
          elementOptions.types = types;
        }
        const element = new PlaceAutocompleteElement(elementOptions);

        if (placeholder) element.placeholder = placeholder;

        element.addEventListener('gmp-select', async (event: any) => {
          const { placePrediction } = event;
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['formattedAddress'] });
          const address = place.formattedAddress;

          if (address) {
            setShadowValue(address);
            hasInvalidValueRef.current = false;
            onChangeRef.current(address);
          }
        });

        element.addEventListener('input', () => {
          const input = element.shadowRoot?.querySelector('input') ?? element.querySelector?.('input');
          const value = input?.value ?? '';
          setShadowValue(value);

          if (allowManualEntry) {
            hasInvalidValueRef.current = false;
            onChangeRef.current(value);
          } else if (value) {
            hasInvalidValueRef.current = true;
          } else {
            hasInvalidValueRef.current = false;
            onChangeRef.current(value);
          }
        });

        element.addEventListener('focusin', (e: any) => {
          setFocused(true);
          onFocusRef.current(e as FocusEvent<HTMLInputElement>);
        });

        element.addEventListener('focusout', (e: any) => {
          setFocused(false);
          setTimeout(() => {
            setTouched(true);
            if (allowManualEntry || !hasInvalidValueRef.current) {
              const input = getInternalInput();
              const value = input?.value ?? '';
              const blurEvent = { ...e, target: { value }, currentTarget: { value } } as unknown as FocusEvent<HTMLInputElement>;
              onBlurRef.current(blurEvent);
            }
          }, 500);
        });

        element.addEventListener('keydown', (e: any) => {
          setTimeout(() => {
            if ((e as KeyboardEvent).key === 'Enter') {
              setTouched(true);
            }
            if (!hasInvalidValueRef.current) {
              onKeyDownRef.current(e as KeyboardEvent<HTMLInputElement>);
            }
          }, 500);
        });

        containerRef.current.appendChild(element);
        autocompleteElementRef.current = element;

        if (propsValue) {
          const input = element.shadowRoot?.querySelector('input') ?? element.querySelector?.('input');
          if (input) input.value = propsValue;
        }
      } catch {
        if (mounted) setGoogleLoadFailed(true);
      }
    }

    initAutocomplete();

    return () => {
      mounted = false;
      if (autocompleteElementRef.current?.parentNode) {
        autocompleteElementRef.current.parentNode.removeChild(autocompleteElementRef.current);
        autocompleteElementRef.current = null;
      }
    };
  }, [googleKey, readOnly]);

  React.useEffect(() => {
    if (typeof propsValue !== 'string' || readOnly) return;
    const input = getInternalInput();
    if (input) input.value = propsValue;
  }, [propsValue, getInternalInput, readOnly]);

  React.useEffect(() => {
    if (autocompleteElementRef.current && placeholder) {
      autocompleteElementRef.current.placeholder = placeholder;
    }
  }, [placeholder]);

  const handleFallbackInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setShadowValue(value);
    onChange(value);
  };

  const handleFallbackBlur = (event: FocusEvent<HTMLInputElement>) => {
    event.persist();
    setFocused(false);
    setTimeout(() => {
      setTouched(true);
      onBlur(event);
    }, 500);
  };

  const handleFallbackFocus = (event: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus(event);
  };

  const requiredSelectionError = hasInvalidValueRef.current ? t.VALIDATION.LOCATION() : '';
  const notRequiredSelectionError = hasInvalidValueRef.current && shadowValue ? t.VALIDATION.LOCATION() : '';

  const selectionError = required ? requiredSelectionError : notRequiredSelectionError;
  const requiredError = required && !shadowValue ? t.VALIDATION.REQUIRED() : '';
  const error = propsError?.message || requiredError || selectionError;

  if (readOnly || googleLoadFailed) {
    return (
      <NvPopover
        placement='top'
        preventOverflow
        content={<ValidationErrorMessage text={error} />}
        show={!!error.length && (readOnly ? focused : touched)}
      >
        <div className={`bs4-input-group ${readOnly ? 'readonly ' : ''}`}>
          <input
            type='text'
            readOnly={readOnly}
            ref={mergeRefs(ref, fallbackInputRef)}
            defaultValue={propsValue || ''}
            placeholder={placeholder}
            onFocus={handleFallbackFocus}
            onBlur={handleFallbackBlur}
            onChange={readOnly ? undefined : handleFallbackInputChange}
            onKeyDown={readOnly ? undefined : onKeyDown}
            className={`bs4-form-control${className ? ` ${className}` : ''}`}
          />
        </div>
      </NvPopover>
    );
  }

  return (
    <NvPopover
      placement='top'
      preventOverflow
      content={<ValidationErrorMessage text={error} />}
      show={!!error.length && touched}
    >
      <div className={`bs4-input-group${className ? ` ${className}` : ''}`}>
        <div ref={containerRef} className='w-100' />
      </div>
    </NvPopover>
  );
});

export default NvGooglePlacesAutocomplete;
